<template>
    <div class="d-inline pr-2 pl-2">
        <b-button variant="outline-primary" size="sm" @click="openModal">
            <!-- <i class="fas fa-folder-plus"></i> -->
            {{ propButton }}
        </b-button>
        <b-modal size="lg" ref="hashtag-details-modal" centered title="Detailed Hashtag Statistics">
            <!-- Page preloader -->
            <PagePreloader v-if="!modalisready">hashtag analytics</PagePreloader>

            <!-- Error management -->
            <CustomAlerts :errors="errors"></CustomAlerts>

            <!-- Displayer -->
            <template v-if="displayResults">
                <h3>
                    Evolution for <code>#{{ propHashtag }}</code>
                </h3>
                <b-button
                    :href="'https://www.instagram.com/explore/tags/' + propHashtag"
                    variant="outline-success"
                    class="gutter-b gutter-t"
                    target="_blank"
                    ><i class="fas fa-external-link-alt"></i> View
                    <code>#{{ propHashtag }}</code> on Instagram</b-button
                >
                <!-- CASE - Post count graph -->
                <div class="card-body">
                    <h3>Total Post Count</h3>
                    <!--begin::Chart-->
                    <apexchart
                        :options="postCountChartOptions"
                        :series="postCountChartSeries"
                        type="area"
                    ></apexchart>
                    <!--end::Chart-->
                </div>
                <!-- CASE - Post count graph -->
                <div class="card-body">
                    <h3>Daily Average Post Count</h3>
                    <!--begin::Chart-->
                    <apexchart
                        :options="DAPCChartOptions"
                        :series="DAPCChartSeries"
                        type="area"
                    ></apexchart>
                    <!--end::Chart-->
                </div>
                <!-- CASE - Avg Likes -->
                <div class="card-body">
                    <h3>Average Likes for Top Posts</h3>
                    <!--begin::Chart-->
                    <apexchart
                        :options="AvgLikesChartOptions"
                        :series="AvgLikesChartSeries"
                        type="area"
                    ></apexchart>
                    <!--end::Chart-->
                </div>
            </template>

            <template v-slot:modal-footer="{ ok, cancel, hide }">
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()">
                    Close
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
// Defaults
import axios from "axios";
// Components
import PagePreloader from "@/view/content/Custom/PagePreloader.vue";
import CustomAlerts from "@/view/content/Custom/CustomAlerts.vue";
import InfoBox from "@/view/content/Custom/InfoBox.vue";
// Mixins
import axiosErrorManagement from "@/mixins/axiosErrorManagement";
import stringManagement from "@/mixins/stringManagement";

export default {
    name: "buttonGetHashtagStatistics",
    mixins: [axiosErrorManagement, stringManagement],
    components: {
        CustomAlerts,
        PagePreloader,
        InfoBox,
    },
    props: {
        propHashtag: String,
        propButton: {
            type: String,
            default: "Hashtag Trends",
        },
    },
    data() {
        return {
            // Errors
            errors: [],

            // Displayers
            modalisready: false,
            displayResults: false,

            // Preloaders
            modalisloading: false,

            // Displayers
            hashtagdata: [],

            // Tables ?

            // Graphs
            // CASE - Post count
            postCountChartOptions: {},
            postCountChartSeries: [],
            // CASE - DAPC
            DAPCChartOptions: {},
            DAPCChartSeries: [],
            // CASE
            AvgLikesChartOptions: {},
            AvgLikesChartSeries: [],
        };
    },
    methods: {
        resetAll() {
            // Reset errors
            this.errors = [];

            // Reset graphs
        },
        getHashtagStatistics() {
            // Perform get
            axios
                .post("hashtags/historical", {
                    hashtag: this.propHashtag,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "OK") {
                        // Set data
                        this.hashtagdata = data.data;

                        /**
                         * SECTION - Handle graphs
                         */
                        var labels = [];
                        var seriesPostCount = [];
                        var seriesDAPC = [];
                        var seriesAvgLikes = [];
                        // Generate all data
                        data.data.forEach((currentItem) => {
                            // Dates
                            labels.push(currentItem.date);
                            // Post count
                            seriesPostCount.push(currentItem.count);
                            // DAPC
                            seriesDAPC.push(currentItem.DAPC_weekly);
                            // AVG Likes
                            seriesAvgLikes.push(currentItem.avgAvgLikes);
                        });
                        // Prepare graphs
                        // CASE - Post count graph
                        this.postCountChartSeries = [
                            {
                                name: "Total Post Count",
                                data: seriesPostCount,
                            },
                        ];
                        this.postCountChartOptions = {
                            chart: {
                                type: "area",
                                height: "auto",
                                zoom: {
                                    enabled: false,
                                },
                            },
                            dataLabels: {
                                enabled: false,
                            },
                            stroke: {
                                curve: "straight",
                            },
                            labels: labels,
                            xaxis: {
                                type: "datetime",
                            },
                            yaxis: {
                                opposite: true,
                                labels: {
                                    formatter: (value) => {
                                        return this.addCommaToNumber(Math.round(value * 100) / 100);
                                    },
                                },
                            },
                            legend: {
                                horizontalAlign: "left",
                            },
                        };

                        // CASE - DAPC Graph
                        this.DAPCChartSeries = [
                            {
                                name: "Daily Average Post Count",
                                data: seriesDAPC,
                            },
                        ];
                        this.DAPCChartOptions = this.postCountChartOptions;

                        // CASE - Avg Likes
                        this.AvgLikesChartSeries = [
                            {
                                name: "Average likes",
                                data: seriesAvgLikes,
                            },
                        ];
                        this.AvgLikesChartOptions = this.postCountChartOptions;

                        // Display results
                        this.displayResults = true;
                    }

                    // Handle errors
                    if (data.messages.length > 0) {
                        data.messages.forEach((currentItem) => {
                            this.errors.push({
                                type: currentItem.type,
                                message: currentItem.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    this.modalisready = true;
                });
        },

        // Open modal
        openModal() {
            // Refresh errors
            this.errors = [];

            // Reset displayer
            this.displayResults = false;

            // Load
            this.getHashtagStatistics();

            // Open modal
            this.$refs["hashtag-details-modal"].show();
        },
    },
    computed: {},
};
</script>
